/**
 * Teacher / character voice-over. Order (docs/VOICE.md):
 *   1. a pre-rendered natural clip from the locale's voice manifest (lib/voice.ts; live flag `preRenderedVoice`) —
 *      `speak()` resolves when the real clip ends, so captions / chapter timing follow the real duration;
 *   2. the Web Speech API (speechSynthesis) at a slower natural rate (locale `ttsRate`, default 0.9) when no clip exists;
 *   3. a deterministic reading time when speech is unavailable, disabled, or `?mockTts=1` is in the URL (tests):
 *      ≈ 60 ms per character, capped, so captions and chapter timing behave the same everywhere.
 */
import { LOCALES, plainText, type Lang, speakableText } from '@sketchling/core';
import { useApp } from '../state/store';
import { clipActive, loadManifest, planFor, playPlan, prefetchSpeech, stats, stopClip } from './voice';

const params = typeof location !== 'undefined' ? new URLSearchParams(location.search) : new URLSearchParams();
if (params.has('mockTts')) sessionStorage.setItem('sk-mock-tts', params.get('mockTts') || '1');
export const MOCK_TTS = typeof sessionStorage !== 'undefined' && !!sessionStorage.getItem('sk-mock-tts');

let current: SpeechSynthesisUtterance | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;
/** Resolver of the line in progress ("resolves when finished or stopped"). */
let pending: (() => void) | null = null;

/** Simulated reading time: ≈ 60 ms per Latin character, ≈ 180 ms per CJK character (one syllable each). */
export const readingTimeMs = (text: string) => {
  const s = plainText(text);
  const cjk = (s.match(/[\u3000-\u9fff\uac00-\ud7af\uf900-\ufaff]/g) ?? []).length;
  return Math.min(5000, Math.max(600, (s.length - cjk) * 60 + cjk * 180));
};

/** A voice for the locale (core registry: Web Speech tag + preferred voice names), else any voice of the language. */
function pickVoice(lang: Lang): SpeechSynthesisVoice | undefined {
  const voices = window.speechSynthesis?.getVoices() ?? [];
  const info = LOCALES[lang] ?? LOCALES.en;
  const tag = info.tts.toLowerCase();
  const base = tag.split('-')[0]!;
  const hints = new RegExp((info.voiceHints ?? ['female', 'google']).map((h) => h.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|'), 'i');
  const exact = voices.filter((v) => v.lang.toLowerCase().replace('_', '-') === tag);
  const family = voices.filter((v) => v.lang.toLowerCase().startsWith(base));
  return exact.find((v) => hints.test(v.name)) ?? exact[0] ?? family.find((v) => hints.test(v.name)) ?? family[0];
}

let seq = 0;

export function stopSpeaking(): void {
  seq++;
  stopClip();
  if (timer) clearTimeout(timer);
  timer = null;
  const p = pending;
  pending = null;
  p?.();
  try {
    window.speechSynthesis?.cancel();
  } catch {
    /* ignore */
  }
  current = null;
}

/** Character voice (story layer): Web Speech pitch / rate. */
export interface VoiceStyle {
  pitch: number;
  rate: number;
}

const clipsOn = () => useApp.getState().config.preRenderedVoice !== false;

/** Speak (or simulate) a line; resolves when finished or stopped. `voice` = a story character's pitch / rate (Web Speech fallback). */
export function speak(text: string, lang?: Lang, voice?: VoiceStyle): Promise<void> {
  stopSpeaking();
  const st = useApp.getState();
  const l = lang ?? st.settings.language;
  const clean = plainText(text);
  if (MOCK_TTS || !st.settings.voice) {
    stats.lastHow = 'timer';
    return new Promise((resolve) => {
      pending = resolve;
      timer = setTimeout(() => {
        pending = null;
        resolve();
      }, MOCK_TTS ? 200 : readingTimeMs(clean));
    });
  }
  const id = seq;
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      if (pending === finish) pending = null;
      resolve();
    };
    pending = finish;
    void (async () => {
      if (clipsOn()) {
        const plan = await planFor(l, clean).catch(() => null);
        if (id !== seq) return;
        if (plan) {
          stats.lastHow = 'clip';
          const r = await playPlan(l, plan);
          if (id !== seq) return;
          if (r !== 'failed') return finish();
        }
        stats.fallback++;
      }
      if (id === seq) webSpeech(clean, l, voice, finish);
    })();
  });
}

function webSpeech(clean: string, l: Lang, voice: VoiceStyle | undefined, finish: () => void): void {
  const synth = typeof window !== 'undefined' ? window.speechSynthesis : undefined;
  if (!synth || typeof SpeechSynthesisUtterance === 'undefined') {
    stats.lastHow = 'timer';
    timer = setTimeout(finish, readingTimeMs(clean));
    return;
  }
  stats.lastHow = 'webSpeech';
  const info = LOCALES[l] ?? LOCALES.en;
  const base = info.ttsRate ?? 0.9;
  // Symbols read naturally ("ω" mouth, →, =, &), emoji silent — the same text the pre-rendered clips say.
  const u = new SpeechSynthesisUtterance(speakableText(clean, l));
  u.lang = info.tts;
  const v = pickVoice(l);
  if (v) u.voice = v;
  u.rate = voice ? Math.max(0.5, Math.min(1.6, voice.rate * base)) : base;
  u.pitch = voice ? Math.max(0.1, Math.min(2, voice.pitch)) : 1.1;
  const end = () => {
    if (current === u) current = null;
    finish();
  };
  u.onend = end;
  u.onerror = end;
  current = u;
  synth.speak(u);
  // Some engines never fire onend: fall back to the reading time.
  timer = setTimeout(end, readingTimeMs(clean) / base + 4000);
}

/** True while a clip or a Web Speech line is playing (music ducking, Juno waits for quiet). */
export const isSpeaking = () => clipActive() || !!current || (typeof window !== 'undefined' && !!window.speechSynthesis?.speaking);

/** Warm the clip cache for lines about to be spoken (lesson open: all of them; while playing: the next 2). */
export function prefetchLines(texts: readonly string[], lang?: Lang): void {
  const st = useApp.getState();
  if (MOCK_TTS || !st.settings.voice || !clipsOn() || !texts.length) return;
  void prefetchSpeech(lang ?? st.settings.language, texts.map((t) => plainText(t)));
}

/** Fetch the locale's voice manifest early (idle after start, language switch). */
export function warmVoice(lang?: Lang): void {
  const st = useApp.getState();
  if (MOCK_TTS || !st.settings.voice || !clipsOn()) return;
  void loadManifest(lang ?? st.settings.language);
}

/** Read-only counters of the clip player (Settings dev panel, e2e/voice.mjs). */
export { stats as voiceStats, prefetchSpeech } from './voice';
